import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import {
  ExtensionsService,
  ProductSellingPlanOption,
} from '@pages/checkout-app/services/extensions/extensions.service';
import { getExtensionRules } from '../../../../../shared/all-extension-rules';
import { DynamicRule, RuleEntity } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { CheckoutProductUpgradeExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import {
  createRuleGroupFormGroup,
  HideProductsAlreadyInCart,
  isFocusedOfferLayout,
  ProductSelection,
  ProductSelectionFormGroup,
  ProductsInCartMode,
  SubscriptionUpgradeBehavior,
  UpgradeFormGroup,
  UpgradeProduct,
  UpgradeProductsMode,
  UpgradeType,
} from '../../models';
import {
  AddProductsModalComponent,
  AddProductsModalConfig,
  SelectableProduct,
} from '@app/sharedModule/product-selection/components/add-products-modal/add-products-modal.component';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { mapExtensionRulesToGqlQuery } from '@pdqdev/pdq-shared/dist/utils/extensions-rules-to-graphql';
import { ShopService } from '@app/services/shops/shop.service';
import { mergeEditedProducts } from '@app/sharedModule/product-selection/utils/merge-edited-products';

@Component({
  selector: 'product-upgrade-product-selection',
  templateUrl: './product-upgrade-product-selection.component.html',
  styleUrls: ['./product-upgrade-product-selection.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class ProductUpgradeProductSelectionComponent implements OnInit, OnDestroy {
  @Input() formGroup: FormGroup<UpgradeFormGroup>;
  @Input() loadedValues: CheckoutProductUpgradeExtension.Template;
  @Input() layoutSwitched = false;

  @Output() switchToFocusedOffer = new EventEmitter<void>();
  @Output() undoLayoutSwitch = new EventEmitter<void>();

  // productId (numeric) → planId (numeric) → plan name, for showing selected plans on rows.
  private sellingPlanNames: Record<string, Record<string, string>> = {};

  private shopService = inject(ShopService);
  private readonly PRODUCT_RELATED_RULE_ENTITIES: RuleEntity[] = [
    RuleEntity.PRODUCT_TYPE,
    RuleEntity.PRODUCT_TAG,
    RuleEntity.SKU,
    RuleEntity.BARCODE,
    RuleEntity.COLLECTION,
    RuleEntity.VENDOR,
  ];
  readonly ruleGroupsFieldName = 'productSelection';
  private destroy$ = new Subject<void>();
  loadingRuleGroupIndex: number | null = null;
  loadingProductsInCartRuleGroupIndex: number | null = null;

  productsInCartRules = getExtensionRules({
    onlyRules: this.PRODUCT_RELATED_RULE_ENTITIES,
  });
  productsInCartEntities = this.productsInCartRules.map((r) => r.entity);

  upgradeProductsRules = getExtensionRules({
    onlyRules: this.PRODUCT_RELATED_RULE_ENTITIES,
  });
  upgradeProductsEntities = this.upgradeProductsRules.map((r) => r.entity);

  hideProductsOptions: { value: HideProductsAlreadyInCart; label: string; description: string }[] = [
    {
      value: 'parentProduct',
      label: 'Parent product only',
      description: 'Hide if any variant of the product is in cart',
    },
    {
      value: 'specificVariant',
      label: 'Specific variant only',
      description: 'Only hide the exact variant in cart (allow other variants)',
    },
  ];

  upgradeTypeOptions: { value: UpgradeType; text: string; description: string }[] = [
    {
      value: 'product',
      text: 'Upgrade to a different product',
      description: 'Swap the cart line item for a higher-value product (existing behavior).',
    },
    {
      value: 'subscription',
      text: 'Upgrade to subscription',
      description:
        'Convert a one-time purchase into a recurring subscription of the same product, using a Shopify selling plan.',
    },
  ];

  subscriptionBehaviorOptions: { value: SubscriptionUpgradeBehavior; text: string; description: string }[] = [
    {
      value: 'oneTime',
      text: 'One time upgrade',
      description: 'Show the subscription offer immediately. Once accepted, the widget is hidden.',
    },
    {
      value: 'sequential',
      text: 'Sequential upgrade',
      description: 'Show the offer first, then a subscription on the upgraded product.',
    },
  ];

  getUpgradeType(index: number): UpgradeType {
    return this.getRuleGroupFormGroup(index)?.get('upgradeProducts.upgradeType')?.value ?? 'product';
  }

  setUpgradeType(index: number, value: UpgradeType): void {
    this.getRuleGroupFormGroup(index)?.controls.upgradeProducts.controls.upgradeType.setValue(value);
  }

  isSubscription(index: number): boolean {
    return this.getUpgradeType(index) === 'subscription';
  }

  get canUseSubscription(): boolean {
    return isFocusedOfferLayout(this.formGroup.controls.settings.getRawValue());
  }

  selectUpgradeType(index: number, value: UpgradeType): void {
    this.setUpgradeType(index, value);
  }

  selectSubscriptionBehavior(index: number, value: SubscriptionUpgradeBehavior): void {
    this.getRuleGroupFormGroup(index)?.controls.upgradeProducts.controls.subscriptionUpgradeBehavior.setValue(value);
  }

  getSubscriptionUpgradeBehavior(index: number): SubscriptionUpgradeBehavior {
    return this.getRuleGroupFormGroup(index)?.get('upgradeProducts.subscriptionUpgradeBehavior')?.value ?? 'oneTime';
  }

  isGroupByKeyEnabled(index: number): boolean {
    return this.getRuleGroupFormGroup(index)?.get('upgradeProducts.groupByKeyEnabled')?.value ?? false;
  }

  toggleGroupByKey(index: number, enabled: boolean): void {
    const upgradeProducts = this.getRuleGroupFormGroup(index)?.controls.upgradeProducts;
    if (!upgradeProducts) return;
    upgradeProducts.controls.groupByKeyEnabled.setValue(enabled);
    const keyControl = upgradeProducts.controls.groupByKey;
    if (enabled) {
      keyControl.addValidators(Validators.required);
    } else {
      keyControl.removeValidators(Validators.required);
      keyControl.setValue('');
    }
    keyControl.updateValueAndValidity();
  }

  constructor(
    private rulesDynamicFormService: RulesDynamicFormService,
    private extensionsService: ExtensionsService,
    private dialog: MatDialog,
  ) {}

  get ruleGroupsArray(): FormArray<FormGroup<ProductSelectionFormGroup>> {
    return this.formGroup?.controls.productSelection;
  }

  ngOnInit(): void {
    if (this.isFlexible) this.loadSellingPlanNames();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSellingPlanNames(): void {
    const productIds = new Set<string>();
    (this.ruleGroupsArray?.controls ?? []).forEach((_, index) => {
      [...this.getProductsInCart(index), ...this.getUpgradeProducts(index)].forEach((product) => {
        if (product?.allowedSellingPlans?.length) {
          productIds.add(String(product.id).split('/').pop() ?? String(product.id));
        }
      });
    });

    const idsToFetch = [...productIds].filter((id) => !this.sellingPlanNames[id]);
    if (!idsToFetch.length) return;

    // Seed so products with no plans (absent from the response) aren't re-fetched on every trigger.
    idsToFetch.forEach((id) => (this.sellingPlanNames[id] = {}));

    this.extensionsService
      .getProductUpgradeSellingPlans(idsToFetch)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (plansByProduct) => {
          for (const [productId, plans] of Object.entries(plansByProduct)) {
            const names: Record<string, string> = {};
            (plans ?? []).forEach((plan) => (names[String(plan.planId)] = this.composeSellingPlanLabel(plan)));
            this.sellingPlanNames[productId] = names;
          }
        },
        // Names are display-only; on failure drop the seeds so a later trigger can retry.
        error: () => idsToFetch.forEach((id) => delete this.sellingPlanNames[id]),
      });
  }

  private buildSellingPlansSummary(product: UpgradeProduct): string {
    const allowed = product?.allowedSellingPlans ?? [];
    if (!allowed.length) return '';
    const numericProductId = String(product.id).split('/').pop() ?? String(product.id);
    const names = this.sellingPlanNames[numericProductId] ?? {};
    const labels = allowed
      .map((planId) =>
        planId === CheckoutProductUpgradeExtension.ONE_TIME_SELLING_PLAN ? 'One-time' : (names[String(planId)] ?? null),
      )
      .filter((label): label is string => !!label);
    return labels.length ? `Plans: ${labels.join(', ')}` : '';
  }

  getRuleGroupFormGroup(index: number): FormGroup<ProductSelectionFormGroup> {
    return this.ruleGroupsArray.at(index);
  }

  addRuleGroup(): void {
    const newId = this.getNextRuleGroupId();
    this.ruleGroupsArray.push(createRuleGroupFormGroup(newId, {}, this.rulesDynamicFormService));
  }

  removeRuleGroup(index: number): void {
    if (this.ruleGroupsArray.length > 1) {
      this.ruleGroupsArray.removeAt(index);
    }
  }

  duplicateRuleGroup(index: number): void {
    const sourceGroup = this.ruleGroupsArray.at(index);
    const newId = this.getNextRuleGroupId();
    const duplicatedValues = { ...sourceGroup.getRawValue(), id: newId } as ProductSelection;
    this.ruleGroupsArray.push(createRuleGroupFormGroup(newId, duplicatedValues, this.rulesDynamicFormService));
  }

  private getNextRuleGroupId(): number {
    const existingIds = this.ruleGroupsArray.value.map((group: { id: number }) => group.id);
    return existingIds.length > 0 ? Math.max(...existingIds) + 1 : 0;
  }

  getProductsInCartMode(index: number): ProductsInCartMode {
    return (
      this.getRuleGroupFormGroup(index)?.get('productsInCart.mode')?.value ??
      CheckoutProductUpgradeExtension.ProductsInCartMode.SMART
    );
  }

  setProductsInCartMode(index: number, mode: ProductsInCartMode): void {
    this.getRuleGroupFormGroup(index)?.controls.productsInCart.controls.mode.setValue(
      mode as CheckoutProductUpgradeExtension.ProductsInCartMode,
    );
  }

  getUpgradeProductsMode(index: number): UpgradeProductsMode {
    return (
      this.getRuleGroupFormGroup(index)?.get('upgradeProducts.mode')?.value ??
      CheckoutProductUpgradeExtension.UpgradeProductsMode.MANUAL
    );
  }

  setUpgradeProductsMode(index: number, mode: UpgradeProductsMode): void {
    this.getRuleGroupFormGroup(index)?.controls.upgradeProducts.controls.mode.setValue(
      mode as CheckoutProductUpgradeExtension.UpgradeProductsMode,
    );
  }

  getUpgradeProducts(index: number): UpgradeProduct[] {
    return this.getRuleGroupFormGroup(index)?.get('upgradeProducts.products')?.value ?? [];
  }

  getUpgradeProductsForDisplay(index: number): (UpgradeProduct & { sellingPlansSummary?: string })[] {
    return this.getUpgradeProducts(index).map((p) => ({
      ...p,
      name: p.displayName || p.name,
      ...(this.isFlexible ? { sellingPlansSummary: this.buildSellingPlansSummary(p) } : {}),
    }));
  }

  getProductsInCart(index: number): UpgradeProduct[] {
    return this.getRuleGroupFormGroup(index)?.get('productsInCart.products')?.value ?? [];
  }

  getProductsInCartForDisplay(index: number): (UpgradeProduct & { sellingPlansSummary?: string })[] {
    return this.getProductsInCart(index).map((p) => ({
      ...p,
      name: p.displayName || p.name,
      ...(this.isFlexible ? { sellingPlansSummary: this.buildSellingPlansSummary(p) } : {}),
    }));
  }

  getRuleGroupSummary(index: number): string {
    const cartMode = this.getProductsInCartMode(index);
    const upgradeMode = this.getUpgradeProductsMode(index);

    let cartCondition = '';
    switch (cartMode) {
      case 'smart':
        cartCondition = 'Smart Selection';
        break;
      case 'manual':
        const cartProducts = this.getProductsInCart(index);
        cartCondition = `Specific Products (${cartProducts.length})`;
        break;
    }

    let upgradeProductsMode = '';
    switch (upgradeMode) {
      case CheckoutProductUpgradeExtension.UpgradeProductsMode.MANUAL:
        const products = this.getUpgradeProducts(index);
        upgradeProductsMode = `Specific Products (${products.length})`;
        break;
      case CheckoutProductUpgradeExtension.UpgradeProductsMode.SMART:
        upgradeProductsMode = 'Smart Selection';
        break;
    }

    return `${cartCondition} → ${upgradeProductsMode}`;
  }

  onAddProductsInCart(index: number): void {
    this.openProductsModal(index, 'productsInCart.products', 'Trigger Products', (query) =>
      firstValueFrom(this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query })),
    );
  }

  onAddUpgradeProducts(index: number): void {
    const mode = this.getUpgradeProductsMode(index);
    this.openProductsModal(index, 'upgradeProducts.products', 'Upgrade Products', (query) =>
      this.fetchUpgradeProducts(index, mode, query),
    );
  }

  private openProductsModal(
    index: number,
    productsPath: 'productsInCart.products' | 'upgradeProducts.products',
    productsLabel: string,
    fetchProducts: (query: string) => Promise<SelectableProduct[]>,
  ): void {
    const productsControl = this.getRuleGroupFormGroup(index)?.get(productsPath);
    const currentProducts: UpgradeProduct[] = productsControl?.value ?? [];
    const enableProductEdit = productsPath === 'upgradeProducts.products';

    const config: AddProductsModalConfig = {
      title: `${currentProducts.length ? 'Edit' : 'Add'} ${productsLabel}`,
      searchPlaceholder: 'Search products by name or SKU...',
      addButtonText: 'Select',
      enableEdit: true,
      allowEmptySelection: true,
      enableProductEdit,
      openProductInShopify: (product) => this.onExternalLink(this.toUpgradeProduct(product)),
      preSelectedProducts: currentProducts.map((product) => ({
        id: product.id,
        title: product.name ?? String(product.id),
        sku: product.sku,
        price: product.price,
        imageUrl: product.imageUrl,
        displayName: product.displayName,
        selectedOptions: product.selectedOptions,
        allowedSellingPlans: product.allowedSellingPlans,
      })),
      enableVariants: true,
      enableSellingPlans: this.isFlexible,
      fetchSellingPlans: (productId) => this.fetchSellingPlansForProduct(productId),
      fetchProducts,
    };

    const dialogRef = this.dialog.open(AddProductsModalComponent, {
      data: config,
      panelClass: 'add-products-modal-container',
      width: '700px',
      maxHeight: '80vh',
    });

    dialogRef.afterClosed().subscribe((result: SelectableProduct[] | undefined) => {
      if (!result) return;
      productsControl?.setValue(
        mergeEditedProducts(
          currentProducts,
          result,
          (product) => this.toUpgradeProduct(product),
          (kept, selected) => ({
            ...kept,
            selectedOptions: selected.selectedOptions,
            ...(enableProductEdit ? { displayName: selected.displayName } : {}),
            ...(this.isFlexible ? { allowedSellingPlans: selected.allowedSellingPlans } : {}),
          }),
        ),
      );
      if (this.isFlexible) this.loadSellingPlanNames();
    });
  }

  private toUpgradeProduct(product: SelectableProduct): UpgradeProduct {
    return {
      id: product.id,
      name: product.title,
      sku: product.sku,
      price: product.price,
      imageUrl: product.imageUrl,
      ...(product.displayName ? { displayName: product.displayName } : {}),
      ...(product.selectedOptions ? { selectedOptions: product.selectedOptions } : {}),
      ...(product.allowedSellingPlans ? { allowedSellingPlans: product.allowedSellingPlans } : {}),
    };
  }

  private async fetchUpgradeProducts(
    index: number,
    mode: UpgradeProductsMode,
    searchQuery: string,
  ): Promise<SelectableProduct[]> {
    if (mode === CheckoutProductUpgradeExtension.UpgradeProductsMode.MANUAL) {
      return firstValueFrom(
        this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query: searchQuery }),
      );
    }

    if (mode === CheckoutProductUpgradeExtension.UpgradeProductsMode.SMART) {
      const rules = this.getUpgradeProductsRules(index);
      const rulesQuery = mapExtensionRulesToGqlQuery(rules);
      return firstValueFrom(this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query: rulesQuery }));
    }

    return [];
  }

  get isFlexible(): boolean {
    const settings = this.formGroup.controls.settings.controls;
    return settings.allowSubscriptions.value === true && settings.subscriptionFrequency.value === 'flexible';
  }

  private async fetchSellingPlansForProduct(
    productId: string | number,
  ): Promise<{ label: string; value: string; checked: boolean }[]> {
    const numericId = String(productId).split('/').pop() ?? String(productId);
    const plansByProduct = await firstValueFrom(this.extensionsService.getProductUpgradeSellingPlans([numericId]));
    const plans = plansByProduct[numericId] ?? [];
    if (!plans.length) return [];

    return [
      {
        label: 'One-time purchase',
        value: CheckoutProductUpgradeExtension.ONE_TIME_SELLING_PLAN,
        checked: true,
      },
      ...plans.map((plan) => ({
        label: this.composeSellingPlanLabel(plan),
        value: plan.planId,
        checked: true,
      })),
    ];
  }

  private composeSellingPlanLabel(plan: ProductSellingPlanOption): string {
    const base =
      plan.planName ||
      (plan.interval ? `Every ${plan.intervalCount ?? 1} ${plan.interval.toLowerCase()}` : 'Subscription');
    return plan.groupName ? `${base} (${plan.groupName})` : base;
  }

  private getProductsInCartRules(index: number): DynamicRule[] {
    const rulesFormArray = this.getRuleGroupFormGroup(index)?.get('productsInCart.rules');
    if (!rulesFormArray) {
      return [];
    }
    return rulesFormArray.value as DynamicRule[];
  }

  private getUpgradeProductsRules(index: number): DynamicRule[] {
    const rulesFormArray = this.getRuleGroupFormGroup(index)?.get('upgradeProducts.rules');
    if (!rulesFormArray) {
      return [];
    }
    return rulesFormArray.value as DynamicRule[];
  }

  onExternalLink(product: UpgradeProduct): void {
    const productId =
      typeof product.id === 'string' && product.id.includes('/') ? product.id.split('/').pop() : product.id;

    const shop = this.shopService.shopSignal();
    const trimmedShopName = shop.name.split('.myshopify.com')[0];
    window.open(`https://admin.shopify.com/store/${trimmedShopName}/products/${productId}`, '_blank');
  }

  onViewProductsInCart(index: number): void {
    this.loadingProductsInCartRuleGroupIndex = index;
    const rules = this.getProductsInCartRules(index);
    const query = mapExtensionRulesToGqlQuery(rules);
    this.extensionsService
      .queryShopifyForProductsUsingRestrictionRules({ query })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (products) => {
          const cartProducts: UpgradeProduct[] = products.map((p, idx) => ({
            id: p.id,
            name: p.title,
            imageUrl: p.imageUrl,
            priority: idx,
          }));
          this.getRuleGroupFormGroup(index)?.get('productsInCart.products')?.setValue(cartProducts);
          this.loadingProductsInCartRuleGroupIndex = null;
        },
        error: (err) => {
          console.error('Failed to fetch products in cart:', err);
          this.loadingProductsInCartRuleGroupIndex = null;
        },
      });
  }

  isLoadingProductsInCart(index: number): boolean {
    return this.loadingProductsInCartRuleGroupIndex === index;
  }

  onViewUpgradeProducts(index: number): void {
    this.loadingRuleGroupIndex = index;
    const rules = this.getUpgradeProductsRules(index);
    const query = mapExtensionRulesToGqlQuery(rules);
    this.extensionsService
      .queryShopifyForProductsUsingRestrictionRules({ query })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (products) => {
          const upgradeProducts: UpgradeProduct[] = products.map((p, idx) => ({
            id: p.id,
            name: p.title,
            imageUrl: p.imageUrl,
            priority: idx,
          }));
          this.getRuleGroupFormGroup(index)?.get('upgradeProducts.products')?.setValue(upgradeProducts);
          this.loadingRuleGroupIndex = null;
        },
        error: (err) => {
          console.error('Failed to fetch products:', err);
          this.loadingRuleGroupIndex = null;
        },
      });
  }

  isLoadingProducts(index: number): boolean {
    return this.loadingRuleGroupIndex === index;
  }

  getUpgradeProductsHelperText(mode: UpgradeProductsMode): string {
    switch (mode) {
      case CheckoutProductUpgradeExtension.UpgradeProductsMode.MANUAL:
        return 'Select specific products that will trigger the upgrade offer.';
      case CheckoutProductUpgradeExtension.UpgradeProductsMode.SMART:
        return 'Existing and future products that match the conditions you set';
      default:
        return '';
    }
  }

  getHideProductsValue(index: number): HideProductsAlreadyInCart {
    return this.getRuleGroupFormGroup(index)?.get('hideProductsAlreadyInCart')?.value ?? 'none';
  }

  setHideProductsValue(index: number, value: HideProductsAlreadyInCart): void {
    this.getRuleGroupFormGroup(index)?.get('hideProductsAlreadyInCart')?.setValue(value);
  }

  toggleHideProducts(index: number, enabled: boolean): void {
    if (enabled) {
      this.setHideProductsValue(index, 'parentProduct');
    } else {
      this.setHideProductsValue(index, 'none');
    }
  }
}
